import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  SystemAdminDataService,
  SystemAdminOrgSubscription,
  SUBSCRIPTION_STATUS_LABELS,
  capacityState,
  NewOrganisationResult,
} from '../../services/systemAdminDataService';
import NewOrganisationModal from '../../components/admin/NewOrganisationModal';

// Capacity is state, not just a number — encode it in form so an over-limit
// setting is visible while scanning rather than only on reading. The hues are
// the app's existing amber (draft/nag) and clay (urgent) families, not new
// ones: this area's lime is reserved for chrome and never enters a table.
const CAPACITY_STYLE = {
  ok:   { state: '' },
  near: { state: 'sys-state--warn' },
  over: { state: 'sys-state--over' },
};

/**
 * System Admin → Organisations: every organisation on the platform with its
 * tier, child roll and AI entitlement. Clicking one opens its detail page.
 *
 * Read-only. Tier and entitlement changes live on the org's Subscription tab.
 */
const Organisations: React.FC = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState<SystemAdminOrgSubscription[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  // The soft-cap chase list, one click away.
  const [overOnly, setOverOnly] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  // What the system admin needs to know after creating one: whether an
  // invitation went out, and whether the welcome email did.
  const onCreated = (result: NewOrganisationResult, orgName: string, adminEmail: string) => {
    setShowNew(false);
    const signIn = result.invited
      ? `${adminEmail} has been sent an invitation with a temporary password.`
      : `${adminEmail} already uses Rustle It and will see ${orgName} next time they sign in.`;
    setNotice(`${orgName} created. ${signIn}${result.notified ? '' : " The welcome email couldn't be sent, so let them know directly."}`);
    void load();
  };

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await SystemAdminDataService.fetchOrgSubscriptions();
      setRows(data);
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Failed to load organisations.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const overCount = useMemo(
    () => rows.filter(r => capacityState(r.childCounts.onRoll, r.subscription?.maxChildren ?? null) === 'over').length,
    [rows]
  );

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    let out = rows;
    if (q) {
      out = out.filter(r =>
        [r.name, r.slug, r.email || '', r.subscription?.tierName || '']
          .join(' ').toLowerCase().includes(q)
      );
    }
    if (overOnly) {
      out = out.filter(r => capacityState(r.childCounts.onRoll, r.subscription?.maxChildren ?? null) === 'over');
    }
    return out;
  }, [rows, search, overOnly]);

  return (
    <div className="sys-page">
      <div className="sys-page__head">
        <p className="sys-page__eyebrow">Platform</p>
        <h1 className="sys-page__title">Organisations</h1>
        <span className="sys-page__count">
          {loading ? 'Loading…'
            : visible.length === rows.length
              ? `${rows.length} ${rows.length === 1 ? 'organisation' : 'organisations'}`
              : `${visible.length} of ${rows.length}`}
        </span>
      </div>

      <div className="pill-toolbar" role="toolbar" aria-label="Organisation filters">
        <div className="pill-toolbar__search">
          <i className="fas fa-search" aria-hidden="true" />
          <input
            type="text"
            placeholder="Search by name, slug, email or tier"
            value={search}
            onChange={e => setSearch(e.target.value)}
            aria-label="Search organisations"
          />
        </div>
        {overCount > 0 && (
          <button
            type="button"
            className="sys-filter-btn ms-2"
            onClick={() => setOverOnly(v => !v)}
            aria-pressed={overOnly}
            title="Show only organisations over their child limit"
          >
            <i className="fas fa-triangle-exclamation" aria-hidden="true" />
            {overCount} over limit
          </button>
        )}
        <div className="pill-toolbar__spacer" />
        <button type="button" className="btn btn-sm btn-primary me-2" onClick={() => { setNotice(null); setShowNew(true); }}>
          <i className="fas fa-plus me-1" aria-hidden="true" />New organisation
        </button>
        <button
          className="pill-toolbar__icon-btn"
          onClick={load}
          disabled={loading}
          title="Reload"
          aria-label="Reload organisations"
        >
          <i className="fas fa-sync-alt" style={loading ? { animation: 'fa-spin-smooth 0.7s linear infinite' } : undefined} />
        </button>
      </div>

      <div className="sys-page__body">
        {notice && <div className="alert alert-success py-2" role="status">{notice}</div>}
        {error ? (
          <div className="alert alert-danger">{error}</div>
        ) : loading && rows.length === 0 ? (
          <div className="d-flex justify-content-center align-items-center py-5">
            <div className="spinner-border text-success" role="status">
              <span className="visually-hidden">Loading…</span>
            </div>
          </div>
        ) : rows.length === 0 ? (
          <div className="ivory-panel sys-panel sys-panel--pad text-center text-muted py-5">
            No organisations yet.
          </div>
        ) : (
          <div className="ivory-panel sys-panel sys-panel--table">
            <div className="sys-table-scroll">
            <table className="sys-table sys-table--rows">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Name</th>
                  <th>Slug</th>
                  <th>Email</th>
                  <th>Tier</th>
                  <th className="sys-table__num">Children</th>
                  <th>AI</th>
                  <th>Subscription</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {visible.map(r => {
                  const sub = r.subscription;
                  const cap = capacityState(r.childCounts.onRoll, sub?.maxChildren ?? null);
                  const capStyle = CAPACITY_STYLE[cap];
                  const open = () => navigate(`/system-admin/organisations/${r.id}`);
                  return (
                    <tr
                      key={r.id}
                      tabIndex={0}
                      onClick={open}
                      onKeyDown={e => { if (e.key === 'Enter') open(); }}
                      title={`Open ${r.name}`}
                    >
                      <td className="sys-table__id">{r.id}</td>
                      <td className="sys-table__name">{r.name}</td>
                      <td className="sys-table__muted">{r.slug}</td>
                      <td>{r.email ? <a href={`mailto:${r.email}`} onClick={e => e.stopPropagation()}>{r.email}</a> : <span className="sys-table__muted">—</span>}</td>

                      <td>{sub ? (sub.tierName || sub.tierCode) : <span className="sys-state sys-state--over">None</span>}</td>

                      {/* on-roll / permitted. Leavers are excluded by design — they
                          are retained for compliance, not served, so they aren't billed. */}
                      <td
                        className={`sys-table__num ${capStyle.state}`}
                        title={
                          r.childCounts.leavers > 0
                            ? `${r.childCounts.leavers} leaver${r.childCounts.leavers === 1 ? '' : 's'} not counted`
                            : undefined
                        }
                      >
                        {cap === 'over' && <i className="fas fa-triangle-exclamation me-1" aria-hidden="true" />}
                        {r.childCounts.onRoll}
                        <span className="sys-table__muted"> / {sub?.maxChildren ?? '∞'}</span>
                      </td>

                      {/* Effective entitlement, with the override called out — a
                          one-off concession should never be invisible in the list. */}
                      <td>
                        {sub?.aiFullEnabled ? (
                          <span className="sys-state sys-state--on"><i className="fas fa-wand-magic-sparkles" />Full</span>
                        ) : (
                          <span className="sys-state sys-state--off"><i className="fas fa-pen" />Rewrite only</span>
                        )}
                        {sub?.aiFullIsOverride && (
                          <span className="sys-tag" title="Set for this organisation, not inherited from its tier">
                            override
                          </span>
                        )}
                      </td>

                      <td className="sys-table__muted">
                        {sub ? SUBSCRIPTION_STATUS_LABELS[sub.status] ?? sub.status : '—'}
                      </td>

                      <td>
                        {r.isActive ? (
                          <span className="sys-state sys-state--on"><i className="fas fa-check-circle" />Active</span>
                        ) : (
                          <span className="sys-state sys-state--off"><i className="fas fa-minus-circle" />Inactive</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
                {visible.length === 0 && (
                  <tr>
                    <td colSpan={9} className="sys-table__empty">
                      {overOnly ? 'No organisations are over their child limit.' : 'No organisations match your search.'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
            </div>
          </div>
        )}
      </div>
      <NewOrganisationModal show={showNew} onClose={() => setShowNew(false)} onCreated={onCreated} />
    </div>
  );
};

export default Organisations;
